import React from "react";
import { Button, TextField, IconButton, Typography } from "@material-ui/core";
import {
  Delete,
  ArrowUpward,
  ArrowDownward,
  Event as EventIcon,
  Schedule as ScheduleIcon,
  Class as ClassIcon,
} from "@material-ui/icons";
import MultilanguageTabBlock from "../../../components/MultilanguageTabBlock";
import CongresoRedesSocialesEditor from "./CongresoRedesSocialesEditor";
import CongresoSeccionAnchoGrillaEditor from "./CongresoSeccionAnchoGrillaEditor";
import { normalizeCongresoAnchoGrilla } from "./congresoAnchoGrilla";
import { withTalleresDiasSortedPorHora } from "./congresoHoraSort";

function emptyTaller() {
  const tallerId =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `taller_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  return {
    tallerId,
    titulo: {},
    ponente: {},
    redesSociales: [],
    descripcion: {},
    sala: "",
    enlaceUrl: "",
    capacidad: null,
    anchoGrilla: 100,
  };
}

function diasList(raw) {
  return Array.isArray(raw) ? raw : [];
}

/** Misma idea que `diaPillPreview` en programa científico */
function diaPillPreview(dia, index) {
  const e = dia.etiqueta || {};
  const t = (e.es || e.ca || e.en || "").trim();
  return t || `Día ${index + 1}`;
}

export default function CongresoTalleresEditor({ talleresPorDias = [], onChange, readOnly = false }) {
  const persistSorted = (next) => onChange(withTalleresDiasSortedPorHora(next));
  const persistAsIs = (next) => onChange(next);

  const addDia = () => {
    persistSorted([
      ...diasList(talleresPorDias),
      {
        etiqueta: {},
        franjas: [
          {
            orden: 0,
            hora: "",
            talleres: [emptyTaller()],
          },
        ],
      },
    ]);
  };

  const removeDia = (di) => {
    const n = [...diasList(talleresPorDias)];
    n.splice(di, 1);
    persistSorted(n);
  };

  const moveDia = (di, dir) => {
    const n = [...diasList(talleresPorDias)];
    const j = di + dir;
    if (j < 0 || j >= n.length) return;
    const t = n[di];
    n[di] = n[j];
    n[j] = t;
    persistSorted(n);
  };

  const updateDiaEtiqueta = (di, lang, val) => {
    const n = [...diasList(talleresPorDias)];
    const row = n[di] || { etiqueta: {}, franjas: [] };
    n[di] = {
      ...row,
      etiqueta: { ...(row.etiqueta || {}), [lang]: val },
    };
    persistAsIs(n);
  };

  const franjasOf = (di) => {
    const row = diasList(talleresPorDias)[di];
    return row && Array.isArray(row.franjas) ? row.franjas : [];
  };

  const addFranja = (di) => {
    const n = [...diasList(talleresPorDias)];
    const row = n[di] || { etiqueta: {}, franjas: [] };
    const list = [...(row.franjas || [])];
    list.push({
      orden: list.length,
      hora: "",
      talleres: [emptyTaller()],
    });
    n[di] = { ...row, franjas: list };
    persistSorted(n);
  };

  const removeFranja = (di, fi) => {
    const n = [...diasList(talleresPorDias)];
    const row = n[di];
    if (!row) return;
    const franjas = [...(row.franjas || [])];
    franjas.splice(fi, 1);
    n[di] = {
      ...row,
      franjas: franjas.map((f, i) => ({ ...f, orden: i })),
    };
    persistSorted(n);
  };

  const updateFranjaStr = (di, fi, field, val) => {
    const n = [...diasList(talleresPorDias)];
    const row = n[di];
    if (!row) return;
    const franjas = [...(row.franjas || [])];
    const f = franjas[fi] || {};
    franjas[fi] = { ...f, [field]: val };
    n[di] = { ...row, franjas };
    if (field === "hora") persistAsIs(n);
    else persistSorted(n);
  };

  const sortFranjasDelDia = (di) => {
    const n = [...diasList(talleresPorDias)];
    if (!n[di]) return;
    persistSorted(n);
  };

  const addTaller = (di, fi) => {
    const n = [...diasList(talleresPorDias)];
    const row = n[di];
    if (!row) return;
    const franjas = [...(row.franjas || [])];
    const f = franjas[fi] || { orden: fi, hora: "", talleres: [] };
    franjas[fi] = {
      ...f,
      talleres: [
        ...(f.talleres || []),
        emptyTaller(),
      ],
    };
    n[di] = { ...row, franjas };
    persistSorted(n);
  };

  const removeTaller = (di, fi, ti) => {
    const n = [...diasList(talleresPorDias)];
    const row = n[di];
    if (!row) return;
    const franjas = [...(row.franjas || [])];
    const f = franjas[fi];
    if (!f) return;
    const talleres = [...(f.talleres || [])];
    talleres.splice(ti, 1);
    franjas[fi] = { ...f, talleres };
    n[di] = { ...row, franjas };
    persistSorted(n);
  };

  const updateTallerStr = (di, fi, ti, field, val) => {
    const n = [...diasList(talleresPorDias)];
    const row = n[di];
    if (!row) return;
    const franjas = [...(row.franjas || [])];
    const f = franjas[fi];
    if (!f) return;
    const talleres = [...(f.talleres || [])];
    const t = talleres[ti] || {};
    if (field === "capacidad") {
      const s = String(val).trim();
      if (!s) talleres[ti] = { ...t, capacidad: null };
      else {
        const num = parseInt(s, 10);
        talleres[ti] = {
          ...t,
          capacidad: Number.isFinite(num) && num >= 0 ? num : null,
        };
      }
    } else {
      talleres[ti] = { ...t, [field]: val };
    }
    franjas[fi] = { ...f, talleres };
    n[di] = { ...row, franjas };
    persistSorted(n);
  };

  const updateTallerI18n = (di, fi, ti, field, lang, val) => {
    const n = [...diasList(talleresPorDias)];
    const row = n[di];
    if (!row) return;
    const franjas = [...(row.franjas || [])];
    const f = franjas[fi];
    if (!f) return;
    const talleres = [...(f.talleres || [])];
    const t = talleres[ti] || {};
    talleres[ti] = {
      ...t,
      [field]: { ...(t[field] || {}), [lang]: val },
    };
    franjas[fi] = { ...f, talleres };
    n[di] = { ...row, franjas };
    persistSorted(n);
  };

  const updateTallerRedes = (di, fi, ti, redesSociales) => {
    const n = [...diasList(talleresPorDias)];
    const row = n[di];
    if (!row) return;
    const franjas = [...(row.franjas || [])];
    const f = franjas[fi];
    if (!f) return;
    const talleres = [...(f.talleres || [])];
    const t = talleres[ti] || emptyTaller();
    talleres[ti] = { ...t, redesSociales: Array.isArray(redesSociales) ? redesSociales : [] };
    franjas[fi] = { ...f, talleres };
    n[di] = { ...row, franjas };
    persistSorted(n);
  };

  const updateTallerAnchoGrilla = (di, fi, ti, v) => {
    const n = [...diasList(talleresPorDias)];
    const row = n[di];
    if (!row) return;
    const franjas = [...(row.franjas || [])];
    const f = franjas[fi];
    if (!f) return;
    const talleres = [...(f.talleres || [])];
    const t = talleres[ti] || emptyTaller();
    talleres[ti] = { ...t, anchoGrilla: normalizeCongresoAnchoGrilla(v, 100) };
    franjas[fi] = { ...f, talleres };
    n[di] = { ...row, franjas };
    persistAsIs(n);
  };

  const renderForLang = (lang) => (
    <div className="congreso-edicion-programa-panel congreso-talleres-editor">
      <Typography variant="body2" color="textSecondary" className="mb-3">
        Un bloque por día; dentro, franjas horarias y varios talleres en paralelo por franja.
        Las franjas se ordenan solas por la hora (p. ej. 8:00-9:00 antes que 9:00-10:00).
      </Typography>
      <div className="congreso-edicion-comites-panel">
      <div className="congreso-edicion-programa-dias-grid">
        {diasList(talleresPorDias).map((dia, di) => (
          <div key={di} className="congreso-edicion-comite-card congreso-talleres-editor__dia">
            <div className="d-flex justify-content-between align-items-start mb-2 flex-wrap" style={{ gap: 8 }}>
              <span className="congreso-talleres-editor__dia-title">
                <EventIcon className="congreso-talleres-editor__icon congreso-talleres-editor__icon--dia" aria-hidden />
                <span className="congreso-prog-dia-preview">{diaPillPreview(dia, di)}</span>
              </span>
              <div className="congreso-prog-slots__row-actions">
                <IconButton
                  size="small"
                  onClick={() => moveDia(di, -1)}
                  aria-label="Subir día"
                  disabled={di === 0}
                >
                  <ArrowUpward fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => moveDia(di, 1)}
                  aria-label="Bajar día"
                  disabled={di === diasList(talleresPorDias).length - 1}
                >
                  <ArrowDownward fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => removeDia(di)} aria-label="Quitar día">
                  <Delete fontSize="small" />
                </IconButton>
              </div>
            </div>
            <TextField
              label="Texto del día"
              fullWidth
              variant="outlined"
              margin="dense"
              placeholder="Jueves 23"
              value={(dia.etiqueta && dia.etiqueta[lang]) || ""}
              onChange={(e) => updateDiaEtiqueta(di, lang, e.target.value)}
              InputLabelProps={{ shrink: true }}
            />

            <Typography
              variant="subtitle2"
              component="div"
              className="congreso-talleres-editor__block-heading mt-3 mb-2"
            >
              <ScheduleIcon className="congreso-talleres-editor__icon congreso-talleres-editor__icon--franja" aria-hidden />
              Franjas horarias
            </Typography>
            <div className="congreso-talleres-editor__franjas">
              {franjasOf(di).map((franja, fi) => (
                <div key={fi} className="congreso-talleres-editor__franja">
                  <div className="d-flex flex-wrap align-items-center justify-content-between mb-1" style={{ gap: 8 }}>
                    <strong className="congreso-talleres-editor__section-label">
                      <ScheduleIcon className="congreso-talleres-editor__icon congreso-talleres-editor__icon--franja" aria-hidden />
                      Franja {fi + 1}
                    </strong>
                    <div className="congreso-prog-slots__row-actions">
                      <IconButton
                        size="small"
                        onClick={() => removeFranja(di, fi)}
                        aria-label="Quitar franja"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                  <TextField
                    label="Hora o franja"
                    fullWidth
                    variant="outlined"
                    margin="dense"
                    placeholder="Ej. 10:00 – 11:30"
                    value={franja.hora || ""}
                    onChange={(e) => updateFranjaStr(di, fi, "hora", e.target.value)}
                    onBlur={() => sortFranjasDelDia(di)}
                    InputLabelProps={{ shrink: true }}
                  />
                  <div className="congreso-talleres-editor__talleres">
                    {(franja.talleres || []).map((tal, ti) => (
                      <div key={ti} className="congreso-talleres-editor__taller">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span className="congreso-talleres-editor__section-label congreso-talleres-editor__section-label--sub">
                            <ClassIcon className="congreso-talleres-editor__icon congreso-talleres-editor__icon--taller" aria-hidden />
                            Taller {ti + 1}
                          </span>
                          <IconButton
                            size="small"
                            onClick={() => removeTaller(di, fi, ti)}
                            aria-label="Quitar taller"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </div>
                        <CongresoSeccionAnchoGrillaEditor
                          compact
                          label="Ancho de este taller en la web"
                          value={tal.anchoGrilla ?? 100}
                          disabled={readOnly}
                          onChange={(v) => updateTallerAnchoGrilla(di, fi, ti, v)}
                        />
                        <TextField
                          label="Título del taller"
                          fullWidth
                          variant="outlined"
                          margin="dense"
                          value={(tal.titulo && tal.titulo[lang]) || ""}
                          onChange={(e) =>
                            updateTallerI18n(di, fi, ti, "titulo", lang, e.target.value)
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                          label="Ponente (opcional)"
                          fullWidth
                          variant="outlined"
                          margin="dense"
                          placeholder="Nombre y apellidos o ponentes"
                          value={(tal.ponente && tal.ponente[lang]) || ""}
                          onChange={(e) =>
                            updateTallerI18n(di, fi, ti, "ponente", lang, e.target.value)
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                        <CongresoRedesSocialesEditor
                          redesSociales={tal.redesSociales || []}
                          onChange={(next) => updateTallerRedes(di, fi, ti, next)}
                          className="mt-1"
                        />
                        <TextField
                          label="Descripción (opcional)"
                          fullWidth
                          variant="outlined"
                          margin="dense"
                          multiline
                          minRows={2}
                          value={(tal.descripcion && tal.descripcion[lang]) || ""}
                          onChange={(e) =>
                            updateTallerI18n(di, fi, ti, "descripcion", lang, e.target.value)
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                        <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                          <TextField
                            label="Sala"
                            variant="outlined"
                            margin="dense"
                            style={{ flex: "1 1 140px", minWidth: 0 }}
                            value={tal.sala || ""}
                            onChange={(e) => updateTallerStr(di, fi, ti, "sala", e.target.value)}
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            label="Capacidad"
                            variant="outlined"
                            margin="dense"
                            style={{ width: 140 }}
                            type="number"
                            inputProps={{ min: 0 }}
                            placeholder="Vacío = no mostrar"
                            value={tal.capacidad != null ? String(tal.capacidad) : ""}
                            onChange={(e) =>
                              updateTallerStr(di, fi, ti, "capacidad", e.target.value)
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        </div>
                        <TextField
                          label="Enlace «Ver más» (URL)"
                          fullWidth
                          variant="outlined"
                          margin="dense"
                          placeholder="https://…"
                          value={tal.enlaceUrl || ""}
                          onChange={(e) => updateTallerStr(di, fi, ti, "enlaceUrl", e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </div>
                    ))}
                  </div>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ClassIcon />}
                    onClick={() => addTaller(di, fi)}
                    className="congreso-edicion-btn-orange congreso-talleres-editor__btn-add"
                  >
                    Añadir taller en esta franja
                  </Button>
                </div>
              ))}
            </div>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ScheduleIcon />}
              onClick={() => addFranja(di)}
              className="congreso-edicion-btn-orange mt-2"
            >
              Añadir franja en este día
            </Button>
          </div>
        ))}
      </div>
      </div>
    </div>
  );

  return (
    <>
      {!diasList(talleresPorDias).length ? (
        <fieldset disabled={readOnly} style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}>
        <div className="congreso-prog-slots__empty">
          <Button
            variant="outlined"
            startIcon={<EventIcon />}
            onClick={addDia}
            className="congreso-edicion-btn-orange"
          >
            Añadir día de talleres
          </Button>
        </div>
        </fieldset>
      ) : (
        <>
          <MultilanguageTabBlock
            contentDisabled={readOnly}
            multilanguageTabContent={renderForLang}
          />
          <fieldset disabled={readOnly} style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}>
          <div className="mt-2">
            <Button
              variant="outlined"
              startIcon={<EventIcon />}
              onClick={addDia}
              className="congreso-edicion-btn-orange"
            >
              Añadir otro día
            </Button>
          </div>
          </fieldset>
        </>
      )}
    </>
  );
}
